<script setup lang="ts">
import { UiText, UiButton, UiIcon, PartnerChip, type PartnerBadge } from "@shopexperts/shared";
/** @ts-ignore */
import { Container, Draggable } from "vue3-smooth-dnd";

interface PartnerBadgeListProps {
  badges: PartnerBadge[];
  draggable?: boolean;
  orientation?: 'horizontal' | 'vertical';
  emptyText?: string;
  showDelete?: boolean;
}

const props = withDefaults(defineProps<PartnerBadgeListProps>(), {
  draggable: false,
  orientation: 'horizontal',
  emptyText: 'No badges assigned yet',
  showDelete: false,
});

const emit = defineEmits<{
  (e: 'reorder', payload: { removedIndex: number | null; addedIndex: number | null }): void;
  (e: 'delete', badgeId: number): void;
}>();

function onDrop(dropResult: { removedIndex: number | null; addedIndex: number | null }) {
  if (dropResult.removedIndex !== null && dropResult.addedIndex !== null) {
    emit('reorder', dropResult);
  }
}

function onDeleteBadge(badgeId: number) {
  emit('delete', badgeId);
}
</script>

<template>
  <div class="w-full">
    <div v-if="badges.length === 0" class="flex flex-col items-center justify-center py-8 text-center">
      <UiText variant="p-small" as="p" class="text-gray-500" :text="emptyText" />
    </div>

    <Container
      v-else-if="props.draggable"
      @drop="onDrop"
      :orientation="props.orientation"
      drag-class="dragging"
      :animation-duration="300"
      :get-child-payload="(index: number) => badges[index]"
      class="flex flex-row flex-wrap"
    >
      <Draggable v-for="(badge) in props.badges" :key="badge.id || `badge-${badge.name}`">
          <PartnerChip 
            :name="badge.name"
            :icon-url="badge.icon?.cdn_url || badge.icon?.url"
            class="cursor-grab active:cursor-grabbing mr-2"
          >
            <template v-if="props.showDelete" #inline-after>
              <UiButton 
                @click.stop="onDeleteBadge(badge.id)" 
                variant="ghost" 
                shape="icon"
                class="hover:bg-se-danger/10 rounded-full transition-colors translate-x-1/4"
                :title="`Remove ${badge.name}`"
              >
                <template #before_label>
                  <UiIcon name="close" class="w-4 h-4 text-se-dark" />
                </template>
              </UiButton>
            </template>
          </PartnerChip>
      </Draggable>
    </Container>

    <!-- Non-draggable version -->
    <div
      v-else
      class="flex flex-row flex-wrap gap-3"
    >
      <PartnerChip 
        v-for="badge in props.badges" 
        :key="badge.id"
        :name="badge.name"
        :icon-url="badge.icon?.cdn_url || badge.icon?.url"
      >
        <template v-if="props.showDelete" #inline-after>
          <UiButton 
            @click.stop="onDeleteBadge(badge.id)" 
            variant="ghost" 
            shape="icon"
            class="hover:bg-se-danger/10 rounded-full transition-colors translate-x-1/4"
            :title="`Remove ${badge.name}`"
          >
            <template #before_label>
              <UiIcon name="close" class="w-4 h-4 text-se-dark" />
            </template>
          </UiButton>
        </template>
      </PartnerChip>
    </div>
  </div>
</template>

<style scoped>
.dragging {
  opacity: 0.5;
  transform: rotate(2deg);
  cursor: grabbing !important;
}
</style>



